import {Navigate} from 'react-router-dom';
import {lazy, Suspense, ReactNode} from 'react';
import {Elements} from '@stripe/react-stripe-js';
import {loadStripe} from '@stripe/stripe-js/pure';
import BaseLayout from '../layout/BaseLayout';
import {PlanChangeProvider} from '../contexts/PlanChangeContext';
import {SignupEntryGuard} from '../components/signup-entry-guard/SignupEntryGuard';
import {BusinessTypeSelectionSkeleton} from '../components/skeleton/BusinessTypeSelectionSkeleton';
import {GenericErrorPage} from '../pages/errors/GenericErrorPage';
import {InvalidInvitationPage} from '../pages/errors/InvalidInvitationPage';
import {NotFoundPage} from '../pages/errors/NotFoundPage';
import {FormSkeleton} from '../components/skeleton/FormSkeleton';
import {MessageSkeleton} from '../components/skeleton/MessageSkeleton';
import {CardSkeleton} from '../components/skeleton/CardSkeleton';
import {StripeSkeleton} from '../components/skeleton/StripeSkeleton';
import {ChangePlansSkeleton} from '../components/skeleton/ChangePlansSkeleton';
import {SimpleFormSkeleton} from '../components/skeleton/SimpleFormSkeleton';
import {PlanSkeleton} from '../components/skeleton/PlanSkeleton';

type StripeWindow = Window & {
  stripeInit?: {
    prismmPublishableKey?: string;
  };
};

let prismmStripePromise: ReturnType<typeof loadStripe> | null = null;

const getPrismmStripePromise = () => {
  const publishableKey = (window as StripeWindow)?.stripeInit
    ?.prismmPublishableKey;
  if (!publishableKey) {
    return null;
  }
  prismmStripePromise ??= loadStripe(publishableKey);
  return prismmStripePromise;
};

const AccountActivated = lazy(async () => {
  const module = await import('../pages/account-activated/AccountActivated');
  return {default: module.AccountActivated};
});
const BusinessTypeSelection = lazy(async () => {
  const module = await import('../pages/business-type/BusinessTypeSelection');
  return {default: module.BusinessTypeSelection};
});
const CollaboratorBusinessTypeSelection = lazy(async () => {
  const module = await import(
    '../pages/collaborator-business-type/CollaboratorBusinessTypeSelection'
  );
  return {default: module.CollaboratorBusinessTypeSelection};
});
const CollaboratorCreateAccount = lazy(async () => {
  const module = await import(
    '../pages/collaborator-create-account/CollaboratorCreateAccount'
  );
  return {default: module.CollaboratorCreateAccount};
});
const CollaboratorCreatePassword = lazy(async () => {
  const module = await import(
    '../pages/collaborator-create-password/CollaboratorCreatePassword'
  );
  return {default: module.CollaboratorCreatePassword};
});

const TeamMemberCreateAccount = lazy(async () => {
  const module = await import(
    '../pages/team-member-create-account/TeamMemberCreateAccount'
  );
  return {default: module.TeamMemberCreateAccount};
});
const TeamMemberCreatePassword = lazy(async () => {
  const module = await import(
    '../pages/team-member-create-password/TeamMemberCreatePassword'
  );
  return {default: module.TeamMemberCreatePassword};
});
const Home = lazy(async () => {
  const module = await import('../pages/home/Home');
  return {default: module.Home};
});
const ActivatePassword = lazy(async () => {
  const module = await import('../pages/activate-password/ActivatePassword');
  return {default: module.ActivatePassword};
});
const SelectPlans = lazy(async () => {
  const module = await import('../pages/plans/SelectPlans');
  return {default: module.SelectPlans};
});
const Success = lazy(async () => {
  const module = await import('../pages/success/Success');
  return {default: module.Success};
});
const CreateAccount = lazy(async () => {
  const module = await import('../pages/create-account/CreateAccount');
  return {default: module.CreateAccount};
});
const CheckEmail = lazy(async () => {
  const module = await import('../pages/check-email/CheckEmail');
  return {default: module.CheckEmail};
});
const VerifyEmail = lazy(async () => {
  const module = await import('../pages/verify-email/VerifyEmail');
  return {default: module.VerifyEmail};
});
const CreatePassword = lazy(async () => {
  const module = await import('../pages/create-password/CreatePassword');
  return {default: module.CreatePassword};
});
const ForgotPassword = lazy(async () => {
  const module = await import('../pages/forgot-password/ForgotPassword');
  return {default: module.ForgotPassword};
});
const ForgotPasswordSetPassword = lazy(async () => {
  const module = await import(
    '../pages/forgot-password-set-password/ForgotPasswordSetPassword'
  );
  return {default: module.ForgotPasswordSetPassword};
});
const ForgotPasswordSuccess = lazy(async () => {
  const module = await import(
    '../pages/forgot-password-success/ForgotPasswordSuccess'
  );
  return {default: module.ForgotPasswordSuccess};
});
const BillingPage = lazy(async () => {
  const module = await import('../pages/billing/BillingPage');
  return {default: module.BillingPage};
});

const UpgradePage = lazy(async () => {
  const module = await import('../pages/upgrade/UpgradePage');
  return {default: module.UpgradePage};
});

const ChangePlans = lazy(async () => {
  const module = await import('../pages/plans/ChangePlans');
  return {default: module.ChangePlans};
});
const ReactivatePlan = lazy(async () => {
  const module = await import('../pages/plans/ReactivatePlan');
  return {default: module.ReactivatePlan};
});

const DowngradePage = lazy(async () => {
  const module = await import('../pages/downgrade/DowngradePage');
  return {default: module.DowngradePage};
});

const ReactivateBillingPage = lazy(async () => {
  const module = await import('../pages/billing/ReactivateBillingPage');
  return {default: module.ReactivateBillingPage};
});

const ViewPlansPlaceholder = lazy(async () => {
  const module = await import('../pages/view-plans/ViewPlansPlaceholder');
  return {default: module.ViewPlansPlaceholder};
});

const UpdatePaymentMethodPage = lazy(async () => {
  const module = await import(
    '../pages/update-payment-method/UpdatePaymentMethodPage'
  );
  return {default: module.UpdatePaymentMethodPage};
});

const StripeRouteElement = ({children}: {children: ReactNode}) => {
  const stripePromise = getPrismmStripePromise();

  if (!stripePromise) {
    throw new Error('Stripe publishable key is not configured');
  }

  return (
    <Suspense fallback={<StripeSkeleton />}>
      <Elements stripe={stripePromise}>{children}</Elements>
    </Suspense>
  );
};

const BillingRouteElement = () => (
  <StripeRouteElement>
    <BillingPage />
  </StripeRouteElement>
);

const UpgradeRouteElement = () => (
  <StripeRouteElement>
    <UpgradePage />
  </StripeRouteElement>
);

const DowngradeRouteElement = () => (
  <StripeRouteElement>
    <DowngradePage />
  </StripeRouteElement>
);

const ReactivateBillingRouteElement = () => (
  <StripeRouteElement>
    <ReactivateBillingPage />
  </StripeRouteElement>
);

const UpdatePaymentMethodRouteElement = () => (
  <StripeRouteElement>
    <UpdatePaymentMethodPage />
  </StripeRouteElement>
);

// Prefix for new PLG routes - can be changed or removed later during cleanup
export const ROUTE_PREFIX = '/new';
const HOME_BACKGROUND_PATH = '/assets/images/home-bg.png';
const LAYOUT_BACKGROUND_PATH = '/assets/images/layout-bg.png';

export const newPlgRouteChildren = [
  {
    path: `${ROUTE_PREFIX}`,
    element: (
      <BaseLayout
        backgroundImage={HOME_BACKGROUND_PATH}
        transparentBackground={true}
      />
    ),
    errorElement: <GenericErrorPage />,
    children: [
      ...[`${ROUTE_PREFIX}`, `${ROUTE_PREFIX}/login`].map((path) => ({
        path,
        element: (
          <Suspense fallback={<CardSkeleton />}>
            <Home />
          </Suspense>
        ),
      })),
    ],
  },
  {
    path: `${ROUTE_PREFIX}/success`,
    element: <BaseLayout transparentBackground={true} />,
    errorElement: <GenericErrorPage />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<MessageSkeleton />}>
            <Success />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: `${ROUTE_PREFIX}/forgot-password`,
    element: <BaseLayout backgroundImage={LAYOUT_BACKGROUND_PATH} />,
    errorElement: <GenericErrorPage />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<SimpleFormSkeleton />}>
            <ForgotPassword />
          </Suspense>
        ),
      },
      {
        path: 'set-password',
        element: (
          <Suspense fallback={<SimpleFormSkeleton />}>
            <ForgotPasswordSetPassword />
          </Suspense>
        ),
      },
      {
        path: 'success',
        element: (
          <Suspense fallback={<MessageSkeleton />}>
            <ForgotPasswordSuccess />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: `${ROUTE_PREFIX}/signup`,
    element: <BaseLayout backgroundImage={LAYOUT_BACKGROUND_PATH} />,
    errorElement: <GenericErrorPage />,
    children: [
      {
        index: true,
        element: <SignupEntryGuard />,
      },
      {
        path: 'business-type',
        element: (
          <Suspense fallback={<BusinessTypeSelectionSkeleton />}>
            <BusinessTypeSelection />
          </Suspense>
        ),
      },
      {
        path: 'plans',
        element: (
          <Suspense fallback={<PlanSkeleton />}>
            <SelectPlans />
          </Suspense>
        ),
      },
      {
        path: 'create-account',
        element: (
          <Suspense fallback={<FormSkeleton />}>
            <CreateAccount />
          </Suspense>
        ),
      },
      {
        path: 'verify-email',
        element: (
          <Suspense fallback={<MessageSkeleton />}>
            <CheckEmail />
          </Suspense>
        ),
      },
      {
        path: 'create-password',
        element: (
          <Suspense fallback={<FormSkeleton />}>
            <CreatePassword />
          </Suspense>
        ),
      },
      {
        path: 'billing',
        element: <BillingRouteElement />,
      },
    ],
  },
  {
    path: `${ROUTE_PREFIX}/change-plan`,
    element: (
      <PlanChangeProvider>
        <BaseLayout backgroundImage={LAYOUT_BACKGROUND_PATH} />
      </PlanChangeProvider>
    ),
    errorElement: <GenericErrorPage />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<ChangePlansSkeleton />}>
            <ChangePlans />
          </Suspense>
        ),
      },
      {
        path: 'upgrade',
        element: <UpgradeRouteElement />,
      },
      {
        path: 'downgrade',
        element: <DowngradeRouteElement />,
      },
    ],
  },
  {
    path: `${ROUTE_PREFIX}/reactivate`,
    element: (
      <PlanChangeProvider>
        <BaseLayout backgroundImage={LAYOUT_BACKGROUND_PATH} />
      </PlanChangeProvider>
    ),
    errorElement: <GenericErrorPage />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PlanSkeleton />}>
            <ReactivatePlan />
          </Suspense>
        ),
      },
      {path: 'billing', element: <ReactivateBillingRouteElement />},
    ],
  },
];

// New routes we're releasing before the flag is turned on
export const releasedPlgRoutes = [
  {
    path: `${ROUTE_PREFIX}/collaborator/:invitationCode`,
    element: <BaseLayout backgroundImage={LAYOUT_BACKGROUND_PATH} />,
    errorElement: <GenericErrorPage />,
    children: [
      {
        index: true,
        element: <Navigate to="business-type" />,
      },
      {
        path: 'business-type',
        element: (
          <Suspense fallback={<PlanSkeleton />}>
            <CollaboratorBusinessTypeSelection />
          </Suspense>
        ),
      },
      {
        path: 'create-account',
        element: (
          <Suspense fallback={null}>
            <CollaboratorCreateAccount />
          </Suspense>
        ),
      },
      {
        path: 'create-password',
        element: (
          <Suspense fallback={null}>
            <CollaboratorCreatePassword />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: `${ROUTE_PREFIX}/team-member/:invitationCode`,
    element: <BaseLayout backgroundImage={LAYOUT_BACKGROUND_PATH} />,
    errorElement: <GenericErrorPage />,
    children: [
      {
        index: true,
        element: <Navigate to="create-account" />,
      },
      {
        path: 'create-account',
        element: (
          <Suspense fallback={null}>
            <TeamMemberCreateAccount />
          </Suspense>
        ),
      },
      {
        path: 'create-password',
        element: (
          <Suspense fallback={null}>
            <TeamMemberCreatePassword />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: `${ROUTE_PREFIX}/account-activated`,
    element: <BaseLayout />,
    errorElement: <GenericErrorPage />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<MessageSkeleton />}>
            <AccountActivated />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: `${ROUTE_PREFIX}/activate-account-password`,
    element: <BaseLayout backgroundImage={LAYOUT_BACKGROUND_PATH} />,
    errorElement: <GenericErrorPage />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<SimpleFormSkeleton />}>
            <ActivatePassword />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: `${ROUTE_PREFIX}/prismm`,
    element: <BaseLayout backgroundImage={LAYOUT_BACKGROUND_PATH} />,
    errorElement: <GenericErrorPage />,
    children: [
      {
        index: true,
        element: <Navigate to="verify" />,
      },
      {
        path: 'check-email',
        element: (
          <Suspense fallback={<MessageSkeleton />}>
            <CheckEmail />
          </Suspense>
        ),
      },
      {
        path: 'verify',
        element: (
          <Suspense fallback={<SimpleFormSkeleton />}>
            <VerifyEmail />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: `${ROUTE_PREFIX}/error`,
    element: <GenericErrorPage />,
    errorElement: <GenericErrorPage />,
  },
  {
    path: `${ROUTE_PREFIX}/invitation-invalid`,
    element: <InvalidInvitationPage />,
    errorElement: <GenericErrorPage />,
  },
  {
    path: `${ROUTE_PREFIX}/account/update-payment-method`,
    element: (
      <PlanChangeProvider>
        <BaseLayout backgroundImage={LAYOUT_BACKGROUND_PATH} />
      </PlanChangeProvider>
    ),
    errorElement: <GenericErrorPage />,
    children: [
      {
        index: true,
        element: <UpdatePaymentMethodRouteElement />,
      },
    ],
  },
  {
    path: `${ROUTE_PREFIX}/*`,
    element: <NotFoundPage />,
    errorElement: <GenericErrorPage />,
  },
];

// view plans route(s)
export const viewPlansRoutes = [
  {
    path: `${ROUTE_PREFIX}/view-plans`,
    element: <BaseLayout showFooter={false} />,
    errorElement: <GenericErrorPage />,
    children: [
      {
        path: '',
        index: true,
        element: (
          <Suspense fallback={<PlanSkeleton />}>
            <ViewPlansPlaceholder />
          </Suspense>
        ),
      },
    ],
  },
];
